import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionLabel, StatBlock } from "@/components/Primitives";
import { Loader2, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function fmt(n, opts = {}) {
  if (n == null) return "—";
  return n.toLocaleString("en-US", opts);
}

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [reports, setReports] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [{ data: c }, { data: r }] = await Promise.all([
      api.get(`/campaigns/${id}`), api.get(`/campaigns/${id}/reports`),
    ]);
    setCampaign(c); setReports(r);
  };

  useEffect(() => {
    (async () => { try { await load(); } finally { setLoading(false); } })();
  }, [id]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post(`/campaigns/${id}/sync`);
      await load();
      toast.success("Reports refreshed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="p-12 text-sm text-[#555] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading campaign…</div>;
  if (!campaign) return <div className="p-12">Not found.</div>;

  const latest = reports[0]?.metrics;

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1400px]">
      <Link to="/campaigns" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.15em] text-[#555] hover:text-[#0A0A0A] mb-6">
        <ArrowLeft size={12}/> Back to campaigns
      </Link>
      <PageHeader
        eyebrow={`EXT · ${campaign.external_campaign_id || "—"}`}
        title={campaign.name}
        description={`${campaign.advertiser} · ${campaign.objective.toUpperCase()} · $${fmt(campaign.budget_usd)} budget`}
        testid="campaign-detail-header"
        actions={
          campaign.push_status === "accepted" && (
            <button onClick={sync} disabled={syncing} data-testid="sync-report-button"
              className="bg-[#FF3E00] text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#D93500] disabled:opacity-60">
              {syncing ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
              {syncing ? "Syncing" : "Sync report"}
            </button>
          )
        }
      />

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <StatBlock label="Impressions" value={fmt(latest?.impressions)} testid="metric-impressions"/>
        <StatBlock label="Clicks" value={fmt(latest?.clicks)} sublabel={latest ? `CTR ${latest.ctr}%` : ""} testid="metric-clicks"/>
        <StatBlock label="Conversions" value={fmt(latest?.conversions)} testid="metric-conversions"/>
        <StatBlock label="Spend" value={latest ? `$${fmt(latest.spend_usd, { maximumFractionDigits: 0 })}` : "—"} sublabel={latest ? `${latest.delivery_pct}% delivered` : ""} testid="metric-spend"/>
      </div>

      {/* Targeting + payload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <div className="border border-[#E0E0D8] bg-white p-6">
          <SectionLabel>Targeting</SectionLabel>
          <dl className="font-mono text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#555]">Coordinate</dt><dd>{campaign.targeting.latitude.toFixed(4)}, {campaign.targeting.longitude.toFixed(4)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#555]">Radius</dt><dd>{campaign.targeting.radius_m === 0 ? "Exact" : `${campaign.targeting.radius_m} m`}</dd></div>
            <div className="flex justify-between"><dt className="text-[#555]">Time window</dt><dd>Last {campaign.targeting.time_window_days} days</dd></div>
            <div className="flex justify-between"><dt className="text-[#555]">Audience size</dt><dd>{fmt(campaign.targeting.audience_count)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#555]">Flight</dt><dd>{new Date(campaign.flight_start).toLocaleDateString()} → {new Date(campaign.flight_end).toLocaleDateString()}</dd></div>
          </dl>
        </div>
        <div>
          <SectionLabel>Original payload sent</SectionLabel>
          <pre className="bg-[#0A0A0A] text-[#E0E0D8] font-mono p-5 text-[11px] overflow-x-auto max-h-[280px]" data-testid="campaign-payload">
{JSON.stringify(campaign.payload, null, 2)}
          </pre>
        </div>
      </div>

      {/* Report history */}
      <SectionLabel>Report snapshots ({reports.length})</SectionLabel>
      {reports.length === 0 ? (
        <div className="border border-dashed border-[#E0E0D8] p-12 text-center text-sm text-[#555]">
          No report snapshots yet. {campaign.push_status === "accepted" && "Click Sync report to pull the latest."}
        </div>
      ) : (
        <div className="border border-[#E0E0D8] bg-white overflow-x-auto" data-testid="report-snapshots">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A]">
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Synced</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Impressions</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Clicks</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">CTR</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Conv.</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Spend</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Delivery</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Status</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(s => (
                <tr key={s.id} className="border-b border-[#E0E0D8]">
                  <td className="py-3 px-4 font-mono text-[11px] text-[#555]">{new Date(s.metrics.synced_at).toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmt(s.metrics.impressions)}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmt(s.metrics.clicks)}</td>
                  <td className="py-3 px-4 text-right font-mono">{s.metrics.ctr}%</td>
                  <td className="py-3 px-4 text-right font-mono">{fmt(s.metrics.conversions)}</td>
                  <td className="py-3 px-4 text-right font-mono">${fmt(s.metrics.spend_usd, { maximumFractionDigits: 0 })}</td>
                  <td className="py-3 px-4 text-right font-mono">{s.metrics.delivery_pct}%</td>
                  <td className="py-3 px-4">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border text-[#0052FF] border-[#0052FF]/30 bg-[#0052FF]/10">
                      {s.metrics.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
